import { useEffect, useState } from 'react'

import { useQueryClient } from 'react-query'
import type { InfiniteData } from 'react-query'

import { VIDEO_ORIENTATION } from 'store/streaming'
import { type IPostActions } from 'types/interfaces/IPostActions'
import type { IInfinitePage, IPost } from 'types/interfaces/ITypes'

import {
  deletePostCache,
  updatePostLikeCache,
  updatePostTipAmountCache,
} from './post'
import { updatePostPinCache } from './post/update-post-pin-cache'
import { updatePostPurchaseCache } from './post/update-post-purchase-cache'
import { off, on } from './util'

export interface OrientationState {
  angle: number
  type: string
}

export const useOrientation = () => {
  const [state, setState] = useState<OrientationState | null>(null)

  useEffect(() => {
    const screen = window.screen
    let mounted = true

    const onChange = () => {
      if (!mounted) return

      const { orientation } = screen as any
      if (orientation) {
        const { angle, type } = orientation
        setState({ angle, type })
      } else if ((window as any).orientation !== undefined) {
        setState({
          angle: typeof (window as any).orientation === 'number' ? (window as any).orientation : 0,
          type: '',
        })
      } else {
        setState(null)
      }
    }

    on(window, 'orientationchange', onChange)
    onChange()

    return () => {
      mounted = false
      off(window, 'orientationchange', onChange)
    }
  }, [])

  return state
}

export const useVideoPlayerOrientation = (videoOrientation?: VIDEO_ORIENTATION | string) => {
  if (typeof videoOrientation === 'string') {
    videoOrientation = videoOrientation.toUpperCase()
  }

  const deviceOrientation = useOrientation()
  const [playerWidth, setPlayerWidth] = useState('640px')
  const [playerHeight, setPlayerHeight] = useState('360px')

  useEffect(() => {
    if (!deviceOrientation) return

    if (deviceOrientation.type.includes('portrait')) {
      if (videoOrientation === VIDEO_ORIENTATION.PORTRAIT) {
        setPlayerHeight('100%')
        setPlayerWidth('auto')
      } else {
        setPlayerHeight('auto')
        setPlayerWidth('100%')
      }
    } else {
      if (videoOrientation === VIDEO_ORIENTATION.LANDSCAPE) {
        setPlayerHeight('100%')
        setPlayerWidth('auto')
      } else {
        setPlayerHeight('auto')
        setPlayerWidth('100%')
      }
    }
  }, [deviceOrientation, videoOrientation])

  return { deviceOrientation, playerWidth, playerHeight }
}

// -------------------------
// Guarded post actions
// -------------------------

type MutateCtx = { cachedData: InfiniteData<IInfinitePage> | undefined }
const emptyCtx = (): MutateCtx => ({ cachedData: undefined })

export const usePostActions = (
  postId?: string,
  dataQuery?: unknown
): IPostActions => {
  const queryClient = useQueryClient()

  const id =
    typeof postId === 'string' && postId.trim() !== ''
      ? postId
      : undefined

  return {
    onLikeMutate: async () => {
      if (!id) return emptyCtx()

      return await updatePostLikeCache(
        id,
        dataQuery,
        queryClient
      )
    },

    onDeletePostMutate: async () => {
      if (!id) return emptyCtx()

      return await deletePostCache(
        id,
        dataQuery,
        queryClient
      )
    },

    onPinPostMutate: async () => {
      if (!id) return emptyCtx()

      return await updatePostPinCache(
        id,
        dataQuery,
        queryClient
      )
    },

    onPurchasePostMutate: async (
      purchasedPost: IPost
    ) => {
      return await updatePostPurchaseCache(
        purchasedPost,
        dataQuery,
        queryClient
      )
    },

    onTipMutate: async (tipAmount: number) => {
      if (!id) return

      await updatePostTipAmountCache(
        id,
        dataQuery,
        queryClient,
        tipAmount
      )
    }
  }
}